import type { ComponentProps } from 'react'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import type { VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { dialogSurfaceVariants } from '@/components/ui/dialog'
import { typographyVariants } from '@/components/ui/typography'

export type AlertDialogProps = ComponentProps<typeof AlertDialogPrimitive.Root>
export type AlertDialogTriggerProps = ComponentProps<typeof AlertDialogPrimitive.Trigger>
export type AlertDialogPortalProps = ComponentProps<typeof AlertDialogPrimitive.Portal>
export type AlertDialogOverlayProps = ComponentProps<typeof AlertDialogPrimitive.Overlay>
export type AlertDialogContentProps = ComponentProps<typeof AlertDialogPrimitive.Content> &
  VariantProps<typeof dialogSurfaceVariants>
export type AlertDialogHeaderProps = ComponentProps<'div'>
export type AlertDialogFooterProps = ComponentProps<'div'>
export type AlertDialogTitleProps = ComponentProps<typeof AlertDialogPrimitive.Title>
export type AlertDialogDescriptionProps = ComponentProps<typeof AlertDialogPrimitive.Description>
export type AlertDialogActionProps = ComponentProps<typeof AlertDialogPrimitive.Action> &
  VariantProps<typeof buttonVariants>
export type AlertDialogCancelProps = ComponentProps<typeof AlertDialogPrimitive.Cancel> &
  VariantProps<typeof buttonVariants>

function AlertDialog({ ...props }: AlertDialogProps) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
}

function AlertDialogTrigger({ ...props }: AlertDialogTriggerProps) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
}

function AlertDialogPortal({ ...props }: AlertDialogPortalProps) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
}

function AlertDialogOverlay({ className, ...props }: AlertDialogOverlayProps) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[rgba(46,45,47,0.48)] backdrop-blur-[3px]',
        className
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  padding = 'default',
  size = 'small',
  spacing = 'default',
  viewport = 'auto',
  ...props
}: AlertDialogContentProps) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        data-padding={padding}
        data-size={size}
        data-spacing={spacing}
        data-viewport={viewport}
        className={cn(dialogSurfaceVariants({ padding, size, spacing, viewport }), className)}
        {...props}
      />
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({ className, ...props }: AlertDialogHeaderProps) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  )
}

function AlertDialogFooter({ className, ...props }: AlertDialogFooterProps) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

function AlertDialogTitle({ className, ...props }: AlertDialogTitleProps) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(typographyVariants({ variant: 'sectionTitle' }), className)}
      {...props}
    />
  )
}

function AlertDialogDescription({ className, ...props }: AlertDialogDescriptionProps) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(typographyVariants({ variant: 'description' }), className)}
      {...props}
    />
  )
}

function AlertDialogAction({
  className,
  size = 'default',
  variant = 'default',
  ...props
}: AlertDialogActionProps) {
  return (
    <AlertDialogPrimitive.Action
      data-slot="alert-dialog-action"
      data-variant={variant}
      className={cn(buttonVariants({ size, variant }), className)}
      {...props}
    />
  )
}

function AlertDialogCancel({
  className,
  size = 'default',
  variant = 'outline',
  ...props
}: AlertDialogCancelProps) {
  return (
    <AlertDialogPrimitive.Cancel
      data-slot="alert-dialog-cancel"
      data-variant={variant}
      className={cn(buttonVariants({ size, variant }), className)}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
